/**
 * Biology Qualification Modal
 * ===========================
 * Ultra-fast biology result qualification.
 * Flow: Upload PDF → RAS or Anomalies → Done
 * Target: < 10 seconds for qualification
 */

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Upload, Check, AlertTriangle, FileText, Loader2, Camera, Image as ImageIcon } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";
import { compressImageToJpeg, stitchImagesToPdf, MAX_PAGES } from "@/components/patient/BiologyUploadScanner";

// Severity colors
const SEVERITY_COLORS = {
  critical: "bg-red-100 text-red-700 border-red-300",
  moderate: "bg-orange-100 text-orange-700 border-orange-300",
  low: "bg-yellow-100 text-yellow-700 border-yellow-300",
};

const SEVERITY_ICONS = {
  critical: "🔴",
  moderate: "🟠",
  low: "🟢",
};

export default function BiologyQualificationModal({ patientId, orderId, patientName, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Upload, 2: RAS/Anomalies, 3: Select Anomalies
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [processingImages, setProcessingImages] = useState(false);
  const [sampleDate, setSampleDate] = useState(new Date().toISOString().split("T")[0]);
  const [bilanCategory, setBilanCategory] = useState("");
  const [hasAnomalies, setHasAnomalies] = useState(null);
  const [selectedAnomalies, setSelectedAnomalies] = useState([]);
  const [note, setNote] = useState("");
  const [anomalyCategories, setAnomalyCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch anomaly reference data
  useEffect(() => {
    api.get("/lab/anomalies")
      .then(res => setAnomalyCategories(res.data))
      .catch(() => toast.error("Erreur chargement des anomalies"));
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const f = files[0];
    if (f && f.type === "application/pdf") {
      if (!bilanCategory) {
        toast.error("Merci de sélectionner la catégorie du bilan avant d'uploader");
        return;
      }
      if (pages.length > 0) {
        toast.error("Retirez les photos avant de choisir un PDF");
        return;
      }
      setFile(f);
      setStep(2);
    } else {
      toast.error("Veuillez sélectionner un fichier PDF");
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (!bilanCategory) {
      toast.error("Merci de sélectionner la catégorie du bilan avant d'ajouter les photos");
      return;
    }
    if (file) {
      toast.error("Retirez le PDF avant de choisir des photos");
      return;
    }
    if (pages.length + files.length > MAX_PAGES) {
      toast.error(`Maximum ${MAX_PAGES} pages`);
      return;
    }
    setProcessingImages(true);
    const toastId = toast.loading("Préparation des photos…");
    try {
      const newPages = [];
      for (const selected of files) {
        if (!selected.type.startsWith("image/")) continue;
        const { bytes, width, height } = await compressImageToJpeg(selected);
        newPages.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          previewUrl: URL.createObjectURL(new Blob([bytes], { type: "image/jpeg" })),
          bytes,
          width,
          height,
        });
      }
      setPages(prev => [...prev, ...newPages]);
      toast.dismiss(toastId);
      if (newPages.length) toast.success(`${newPages.length} photo(s) ajoutée(s)`);
    } catch {
      toast.dismiss(toastId);
      toast.error("Impossible de traiter les photos");
    } finally {
      setProcessingImages(false);
    }
  };

  const removePage = (id) => {
    setPages(prev => {
      const removed = prev.find(p => p.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter(p => p.id !== id);
    });
  };

  const continueWithPhotos = async () => {
    if (!pages.length) return;
    setProcessingImages(true);
    try {
      const pdfBytes = await stitchImagesToPdf(pages);
      const stitched = new File([new Blob([pdfBytes], { type: "application/pdf" })], `bilan_ide_${Date.now()}.pdf`, { type: "application/pdf" });
      setFile(stitched);
      setStep(2);
    } catch {
      toast.error("Impossible de créer le PDF à partir des photos");
    } finally {
      setProcessingImages(false);
    }
  };

  const handleRAS = () => {
    setHasAnomalies(false);
    setSelectedAnomalies([]);
    handleSubmit(false, []);
  };

  const handleAnomaliesSelected = () => {
    setHasAnomalies(true);
    setStep(3);
  };

  const toggleAnomaly = (key) => {
    setSelectedAnomalies(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSubmit = async (withAnomalies = true, anomalies = selectedAnomalies) => {
    if (!file) {
      toast.error("Aucun fichier prêt à envoyer");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("patientId", patientId);
    if (orderId) formData.append("orderId", orderId);
    formData.append("sampleDate", sampleDate);
    formData.append("bilanCategory", bilanCategory);
    formData.append("hasAnomalies", withAnomalies.toString());
    formData.append("anomalies", anomalies.join(","));
    if (note) formData.append("note", note);

    try {
      const res = await api.post("/lab/results", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const data = res.data;
      if (data.alertGenerated) {
        toast.warning(`Alerte ${data.alertLevel} générée`, {
          description: `Sévérité: ${data.severityLabel}`
        });
      } else {
        toast.success("Résultat enregistré - RAS");
      }
      
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[120] p-4" data-testid="biology-qualification-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden" data-testid="biology-qualification-modal">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Résultat biologique
              </h2>
              {patientName && (
                <p className="text-sm text-slate-500 mt-0.5">{patientName}</p>
              )}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg" data-testid="close-modal">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-2 mt-3">
            {[1, 2, 3].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${
                step >= s ? "bg-emerald-500" : "bg-slate-200"
              }`} />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-4">
              {/* 1) Catégorie EN PREMIER (UX : choisir avant d'uploader) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Catégorie du bilan <span className="text-red-500">*</span>
                </label>
                <select
                  value={bilanCategory}
                  onChange={e => setBilanCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white"
                  data-testid="bilan-category-select"
                >
                  <option value="">— Sélectionner —</option>
                  <option value="PRE_CHEMO">Bilan pré-chimio</option>
                  <option value="SUIVI">Bilan de suivi</option>
                  <option value="AUTRE">Autre</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Choisissez d'abord le type de bilan, puis téléversez le fichier.
                </p>
              </div>

              {/* 2) Date du prélèvement */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date du prélèvement</label>
                <input
                  type="date"
                  value={sampleDate}
                  onChange={e => setSampleDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  data-testid="sample-date-input"
                />
              </div>

              {/* 3) Upload PDF (désactivé tant que catégorie non choisie) */}
              <div className="text-center py-2">
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => {
                      fileInputRef.current.setAttribute("accept", "image/*");
                      fileInputRef.current.setAttribute("capture", "environment");
                      fileInputRef.current.onchange = handlePhotoSelect;
                      fileInputRef.current.click();
                    }}
                    disabled={!bilanCategory || processingImages || !!file || pages.length >= MAX_PAGES}
                    className="py-4 border-2 border-emerald-300 rounded-xl bg-emerald-50 hover:bg-emerald-100 flex flex-col items-center gap-1.5 disabled:opacity-50"
                    data-testid="ide-biology-camera-button"
                    type="button"
                  >
                    <Camera className="w-6 h-6 text-emerald-600" />
                    <span className="text-xs font-medium text-emerald-700">Photo</span>
                  </button>
                  <button
                    onClick={() => {
                      fileInputRef.current.setAttribute("accept", "image/*");
                      fileInputRef.current.removeAttribute("capture");
                      fileInputRef.current.onchange = handlePhotoSelect;
                      fileInputRef.current.click();
                    }}
                    disabled={!bilanCategory || processingImages || !!file || pages.length >= MAX_PAGES}
                    className="py-4 border-2 border-slate-300 rounded-xl bg-slate-50 hover:bg-slate-100 flex flex-col items-center gap-1.5 disabled:opacity-50"
                    data-testid="ide-biology-gallery-button"
                    type="button"
                  >
                    <ImageIcon className="w-6 h-6 text-slate-600" />
                    <span className="text-xs font-medium text-slate-700">Galerie</span>
                  </button>
                  <button
                    onClick={() => {
                      fileInputRef.current.setAttribute("accept", "application/pdf");
                      fileInputRef.current.removeAttribute("capture");
                      fileInputRef.current.onchange = handleFileSelect;
                      fileInputRef.current.click();
                    }}
                    disabled={!bilanCategory || !!file || pages.length > 0}
                    className="py-4 border-2 border-blue-300 rounded-xl bg-blue-50 hover:bg-blue-100 flex flex-col items-center gap-1.5 disabled:opacity-50"
                    data-testid="ide-biology-pdf-button"
                    type="button"
                  >
                    <Upload className="w-6 h-6 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">PDF</span>
                  </button>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" data-testid="file-input" />
                {pages.length > 0 && (
                  <div className="mt-4 space-y-3" data-testid="ide-biology-photo-preview">
                    <div className="grid grid-cols-3 gap-3">
                      {pages.map((p, idx) => (
                        <div key={p.id} className="relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-50" data-testid={`ide-biology-page-${idx}`}>
                          <img src={p.previewUrl} alt={`Page ${idx + 1}`} className="w-full h-full object-cover" />
                          <button onClick={() => removePage(p.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center" data-testid={`ide-biology-remove-page-${idx}`} type="button">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button onClick={continueWithPhotos} disabled={processingImages} className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold disabled:opacity-50" data-testid="ide-biology-continue-photos-button" type="button">
                      {processingImages ? "Préparation…" : `Continuer avec ${pages.length} photo(s)`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: RAS or Anomalies */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-slate-600 text-sm">{file?.name}</p>
              </div>
              
              <p className="text-center font-semibold text-slate-800 text-lg mb-4">
                Interprétation rapide du bilan
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleRAS}
                  disabled={submitting}
                  className="py-6 px-4 border-2 border-emerald-300 rounded-2xl bg-emerald-50 hover:bg-emerald-100 transition-all flex flex-col items-center gap-2 hover:scale-[1.02]"
                  data-testid="ras-btn"
                >
                  <Check className="w-10 h-10 text-emerald-600" />
                  <span className="font-bold text-emerald-700 text-lg">RAS</span>
                  <span className="text-xs text-emerald-600">Aucune anomalie</span>
                </button>
                
                <button
                  onClick={handleAnomaliesSelected}
                  disabled={submitting}
                  className="py-6 px-4 border-2 border-orange-300 rounded-2xl bg-orange-50 hover:bg-orange-100 transition-all flex flex-col items-center gap-2 hover:scale-[1.02]"
                  data-testid="anomalies-btn"
                >
                  <AlertTriangle className="w-10 h-10 text-orange-600" />
                  <span className="font-bold text-orange-700 text-lg">Anomalies</span>
                  <span className="text-xs text-orange-600">Détectées</span>
                </button>
              </div>
              
              {submitting && (
                <div className="flex items-center justify-center gap-2 text-emerald-600 mt-4">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Enregistrement...</span>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select Anomalies */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-center font-semibold text-slate-800 mb-2">
                Sélectionnez les anomalies détectées
              </p>
              <p className="text-center text-sm text-slate-500 mb-4">
                Sélection multiple possible
              </p>
              
              {anomalyCategories.map(category => (
                <div key={category.key} className="mb-4">
                  <p className="font-medium text-slate-700 mb-2 text-sm">{category.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {category.anomalies.map(anomaly => (
                      <button
                        key={anomaly.key}
                        onClick={() => toggleAnomaly(anomaly.key)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                          selectedAnomalies.includes(anomaly.key)
                            ? SEVERITY_COLORS[anomaly.severity]
                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}
                        data-testid={`anomaly-${anomaly.key}`}
                      >
                        {selectedAnomalies.includes(anomaly.key) && (
                          <span className="mr-1">{SEVERITY_ICONS[anomaly.severity]}</span>
                        )}
                        {anomaly.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Optional note */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Précision (optionnel)
                </label>
                <input
                  type="text"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  maxLength={200}
                  placeholder="Commentaire court si nécessaire..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="note-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 3 && (
          <div className="p-4 border-t bg-slate-50 flex gap-3">
            <button
              onClick={() => setStep(2)}
              className="flex-1 py-3 border border-slate-200 rounded-xl text-slate-600 font-medium"
            >
              Retour
            </button>
            <button
              onClick={() => handleSubmit(true, selectedAnomalies)}
              disabled={submitting || selectedAnomalies.length === 0}
              className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-anomalies-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer ({selectedAnomalies.length} anomalie{selectedAnomalies.length > 1 ? "s" : ""})
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
